import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Broadcast, CreateBroadcast, MetricSample } from '@ivs-test/contracts';
import { Link, NavLink, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { api, type TestRun } from './api.js';
import { IvsBroadcaster } from './IvsBroadcaster.js';
import { IvsViewer } from './IvsViewer.js';
import { RecordingPlayer } from './RecordingPlayer.js';

const defaultInput: CreateBroadcast = {
  name: '',
  kind: 'REALTIME',
  recordingEnabled: true,
  encoderProfile: {
    orientation: 'LANDSCAPE',
    width: 1280,
    height: 720,
    fps: 30,
    videoBitrateKbps: 2500,
    audioBitrateKbps: 128,
  },
};

function formatBytes(value?: number): string {
  if (value === undefined) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let amount = value;
  let unit = 0;
  while (amount >= 1024 && unit < units.length - 1) {
    amount /= 1024;
    unit += 1;
  }
  return `${amount.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
}

function modeLabel(broadcast: Broadcast): string {
  return broadcast.kind === 'REALTIME' ? 'Real-Time' : `Low-Latency / ${broadcast.channelType}`;
}

function Layout() {
  return (
    <div className="app-shell">
      <aside className="sidebar">
        <Link className="brand" to="/">
          <span className="brand-mark">IVS</span>
          <span>Validation Console</span>
        </Link>
        <nav>
          <NavLink to="/" end>配信</NavLink>
          <NavLink to="/recordings">録画</NavLink>
          <NavLink to="/measurements">計測</NavLink>
        </nav>
        <div className="sidebar-foot">
          <span>ap-northeast-1</span>
          <span>ivs-test-poc</span>
        </div>
      </aside>
      <main className="main-content">
        <Routes>
          <Route path="/" element={<BroadcastsPage />} />
          <Route path="/broadcasts/:id" element={<BroadcastDetailPage />} />
          <Route path="/recordings" element={<RecordingsPage />} />
          <Route path="/recordings/:id" element={<RecordingDetailPage />} />
          <Route path="/measurements" element={<MeasurementsPage />} />
        </Routes>
      </main>
    </div>
  );
}

function BroadcastsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [showCreate, setShowCreate] = useState(false);
  const [input, setInput] = useState<CreateBroadcast>(defaultInput);
  const broadcasts = useQuery({ queryKey: ['broadcasts'], queryFn: api.listBroadcasts, refetchInterval: 10_000 });
  const createBroadcast = useMutation({
    mutationFn: api.createBroadcast,
    onSuccess: async (broadcast) => {
      await queryClient.invalidateQueries({ queryKey: ['broadcasts'] });
      navigate(`/broadcasts/${broadcast.id}`);
    },
  });

  const submit = () => {
    const normalized: CreateBroadcast =
      input.kind === 'REALTIME'
        ? { ...input, channelType: undefined, transcodePreset: undefined }
        : {
            ...input,
            channelType: input.channelType ?? 'BASIC',
            transcodePreset:
              (input.channelType ?? 'BASIC') === 'ADVANCED_HD'
                ? input.transcodePreset ?? 'HIGHER_BANDWIDTH_DELIVERY'
                : undefined,
          };
    createBroadcast.mutate(normalized);
  };

  return (
    <div>
      <header className="page-header">
        <div>
          <span className="eyebrow">STREAMS</span>
          <h1>配信チャンネル</h1>
          <p>Real-TimeとLow-Latencyを同じ条件で作成し、配信品質を比較します。</p>
        </div>
        <button className="primary" onClick={() => setShowCreate((value) => !value)}>
          {showCreate ? '閉じる' : '新規配信'}
        </button>
      </header>
      {showCreate && (
        <section className="panel create-panel">
          <div className="section-heading"><h2>配信を作成</h2></div>
          <div className="form-grid">
            <label>
              名前
              <input value={input.name} onChange={(event) => setInput({ ...input, name: event.target.value })} />
            </label>
            <label>
              方式
              <select
                value={input.kind}
                onChange={(event) =>
                  setInput({ ...input, kind: event.target.value as CreateBroadcast['kind'], channelType: 'BASIC' })
                }
              >
                <option value="REALTIME">IVS Real-Time</option>
                <option value="LOW_LATENCY">IVS Low-Latency</option>
              </select>
            </label>
            {input.kind === 'LOW_LATENCY' && (
              <>
                <label>
                  Channel Type
                  <select
                    value={input.channelType ?? 'BASIC'}
                    onChange={(event) =>
                      setInput({ ...input, channelType: event.target.value as 'BASIC' | 'ADVANCED_HD' })
                    }
                  >
                    <option value="BASIC">BASIC</option>
                    <option value="ADVANCED_HD">ADVANCED_HD</option>
                  </select>
                </label>
                {(input.channelType ?? 'BASIC') === 'ADVANCED_HD' && (
                  <label>
                    Transcode Preset
                    <select
                      value={input.transcodePreset ?? 'HIGHER_BANDWIDTH_DELIVERY'}
                      onChange={(event) =>
                        setInput({
                          ...input,
                          transcodePreset: event.target.value as CreateBroadcast['transcodePreset'],
                        })
                      }
                    >
                      <option value="HIGHER_BANDWIDTH_DELIVERY">Higher bandwidth</option>
                      <option value="CONSTRAINED_BANDWIDTH_DELIVERY">Constrained bandwidth</option>
                    </select>
                  </label>
                )}
              </>
            )}
            <label>
              向き
              <select
                value={input.encoderProfile.orientation}
                onChange={(event) => {
                  const portrait = event.target.value === 'PORTRAIT';
                  setInput({
                    ...input,
                    encoderProfile: {
                      ...input.encoderProfile,
                      orientation: portrait ? 'PORTRAIT' : 'LANDSCAPE',
                      width: portrait ? 720 : 1280,
                      height: portrait ? 1280 : 720,
                    },
                  });
                }}
              >
                <option value="LANDSCAPE">横</option>
                <option value="PORTRAIT">縦</option>
              </select>
            </label>
            <label>
              FPS
              <select
                value={input.encoderProfile.fps}
                onChange={(event) =>
                  setInput({ ...input, encoderProfile: { ...input.encoderProfile, fps: Number(event.target.value) } })
                }
              >
                <option value={15}>15</option>
                <option value={30}>30</option>
                <option value={60}>60</option>
              </select>
            </label>
            <label>
              Width (px)
              <input
                type="number"
                min={160}
                max={1920}
                value={input.encoderProfile.width}
                onChange={(event) =>
                  setInput({ ...input, encoderProfile: { ...input.encoderProfile, width: Number(event.target.value) } })
                }
              />
            </label>
            <label>
              Height (px)
              <input
                type="number"
                min={160}
                max={1920}
                value={input.encoderProfile.height}
                onChange={(event) =>
                  setInput({ ...input, encoderProfile: { ...input.encoderProfile, height: Number(event.target.value) } })
                }
              />
            </label>
            <label>
              Video bitrate (Kbps)
              <input
                type="number"
                value={input.encoderProfile.videoBitrateKbps}
                onChange={(event) =>
                  setInput({
                    ...input,
                    encoderProfile: { ...input.encoderProfile, videoBitrateKbps: Number(event.target.value) },
                  })
                }
              />
            </label>
            <label>
              Audio bitrate (Kbps)
              <input
                type="number"
                min={96}
                max={320}
                value={input.encoderProfile.audioBitrateKbps}
                onChange={(event) =>
                  setInput({
                    ...input,
                    encoderProfile: { ...input.encoderProfile, audioBitrateKbps: Number(event.target.value) },
                  })
                }
              />
            </label>
            <label className="check-row">
              <input
                type="checkbox"
                checked={input.recordingEnabled}
                onChange={(event) => setInput({ ...input, recordingEnabled: event.target.checked })}
              />
              S3へ自動録画
            </label>
          </div>
          <div className="actions">
            <button className="primary" disabled={!input.name || createBroadcast.isPending} onClick={submit}>
              {createBroadcast.isPending ? '作成中…' : '作成'}
            </button>
          </div>
          {createBroadcast.error && <p className="error">{createBroadcast.error.message}</p>}
        </section>
      )}
      <section className="card-grid">
        {broadcasts.data?.items.map((broadcast) => (
          <Link className="stream-card" key={broadcast.id} to={`/broadcasts/${broadcast.id}`}>
            <div className="stream-card-top">
              <span className="mode-badge">{modeLabel(broadcast)}</span>
              <span className={`status ${broadcast.status === 'LIVE' ? 'live' : ''}`}>{broadcast.status}</span>
            </div>
            <h2>{broadcast.name}</h2>
            <p>{broadcast.encoderProfile.width}×{broadcast.encoderProfile.height} / {broadcast.encoderProfile.fps}fps / {broadcast.encoderProfile.videoBitrateKbps}Kbps</p>
            <small>{new Date(broadcast.createdAt).toLocaleString('ja-JP')}</small>
          </Link>
        ))}
      </section>
      {broadcasts.isLoading && <p className="muted">読み込み中…</p>}
    </div>
  );
}

function SecretValue({ value }: { value?: string | undefined }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="secret-value">
      <code>{visible ? value ?? '—' : value ? '•'.repeat(Math.min(value.length, 28)) : '—'}</code>
      <button onClick={() => setVisible((current) => !current)}>{visible ? '隠す' : '表示'}</button>
      {value && <button onClick={() => void navigator.clipboard.writeText(value)}>コピー</button>}
    </div>
  );
}

function BroadcastDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const broadcast = useQuery({ queryKey: ['broadcast', id], queryFn: () => api.getBroadcast(id) });
  const ingest = useQuery({
    queryKey: ['ingest', id],
    queryFn: () => api.getIngest(id),
    enabled: broadcast.data?.kind === 'LOW_LATENCY',
    staleTime: 0,
  });
  const remove = useMutation({
    mutationFn: () => api.deleteBroadcast(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['broadcasts'] });
      navigate('/');
    },
  });
  if (broadcast.isLoading) return <p>読み込み中…</p>;
  if (!broadcast.data) return <p className="error">Broadcast not found</p>;
  const item = broadcast.data;
  const ingestData = ingest.data?.kind === 'LOW_LATENCY' ? ingest.data : undefined;
  return (
    <div>
      <header className="page-header">
        <div>
          <Link className="back-link" to="/">← 配信一覧</Link>
          <span className="eyebrow">{modeLabel(item)}</span>
          <h1>{item.name}</h1>
          <p>{item.id}</p>
        </div>
        <button className="danger" onClick={() => confirm('このIVSリソースだけを削除しますか？') && remove.mutate()}>
          配信を削除
        </button>
      </header>
      <section className="panel">
        <div className="section-heading"><h2>設定</h2><span className="status">{item.status}</span></div>
        <dl className="detail-grid">
          <div><dt>Resource ARN</dt><dd><code>{item.resourceArn}</code></dd></div>
          <div><dt>Recording</dt><dd>{item.recordingEnabled ? 'Enabled' : 'Disabled'}</dd></div>
          <div><dt>Resolution</dt><dd>{item.encoderProfile.width}×{item.encoderProfile.height}</dd></div>
          <div><dt>Encoding</dt><dd>{item.encoderProfile.fps}fps / {item.encoderProfile.videoBitrateKbps}Kbps</dd></div>
        </dl>
        {ingestData && (
          <div className="credentials">
            <label>RTMPS server<SecretValue value={ingestData.rtmpsServerUrl} /></label>
            <label>Stream key<SecretValue value={ingestData.streamKey} /></label>
            <label>RTMPS URL<SecretValue value={ingestData.rtmpsUrl} /></label>
            <label>SRT URL<SecretValue value={ingestData.srtUrl} /></label>
          </div>
        )}
      </section>
      <div className="two-columns">
        <IvsBroadcaster broadcast={item} />
        <IvsViewer broadcast={item} />
      </div>
    </div>
  );
}

function RecordingsPage() {
  const recordings = useQuery({ queryKey: ['recordings'], queryFn: () => api.listRecordings(), refetchInterval: 15_000 });
  return (
    <div>
      <header className="page-header">
        <div><span className="eyebrow">ARCHIVE</span><h1>録画</h1><p>IVSがS3に作成したHLSの形式と品質を確認します。</p></div>
      </header>
      <div className="table-wrap">
        <table>
          <thead><tr><th>方式</th><th>状態</th><th>形式</th><th>長さ</th><th>容量</th><th>Renditions</th></tr></thead>
          <tbody>
            {recordings.data?.items.map((recording) => (
              <tr key={recording.id}>
                <td><Link to={`/recordings/${recording.id}`}>{recording.kind}</Link></td>
                <td>{recording.status}</td><td>{recording.format}</td>
                <td>{recording.durationMs ? `${(recording.durationMs / 1000).toFixed(1)}s` : '—'}</td>
                <td>{formatBytes(recording.totalBytes)}</td><td>{recording.renditions.length}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RecordingDetailPage() {
  const { id = '' } = useParams();
  const recording = useQuery({ queryKey: ['recording', id], queryFn: () => api.getRecording(id) });
  if (!recording.data) return <p>読み込み中…</p>;
  const item = recording.data;
  return (
    <div>
      <header className="page-header"><div><Link className="back-link" to="/recordings">← 録画一覧</Link><span className="eyebrow">{item.format}</span><h1>録画詳細</h1><p>{item.keyPrefix}</p></div></header>
      <div className="two-columns">
        <section className="panel"><RecordingPlayer recordingId={item.id} /></section>
        <section className="panel">
          <dl className="detail-grid single">
            <div><dt>Status</dt><dd>{item.status}</dd></div>
            <div><dt>Duration</dt><dd>{item.durationMs ? `${(item.durationMs / 1000).toFixed(2)} sec` : '—'}</dd></div>
            <div><dt>Total size</dt><dd>{formatBytes(item.totalBytes)}</dd></div>
            <div><dt>Estimated bitrate</dt><dd>{item.estimatedBitrateKbps?.toFixed(1) ?? '—'} Kbps</dd></div>
          </dl>
          <h3>Renditions</h3>
          <ul className="rendition-list">
            {item.renditions.map((rendition) => <li key={`${rendition.path}/${rendition.playlist}`}><code>{rendition.width}×{rendition.height}</code> {rendition.path}</li>)}
          </ul>
          {item.errorMessage && <p className="error">{item.errorMessage}</p>}
        </section>
      </div>
    </div>
  );
}

function downloadCsv(samples: MetricSample[], runId: string) {
  const keys = Array.from(new Set(samples.flatMap((sample) => Object.keys(sample))));
  const escape = (value: unknown) => `"${String(value ?? '').replaceAll('"', '""')}"`;
  const csv = [keys.join(','), ...samples.map((sample) => keys.map((key) => escape(sample[key as keyof MetricSample])).join(','))].join('\n');
  const link = document.createElement('a');
  link.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  link.download = `ivs-test-${runId}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
}

function downloadSummaryCsv(summary: Record<string, unknown>, runId: string) {
  const rows = Object.entries(summary).flatMap(([metric, value]) => {
    if (value && typeof value === 'object') {
      return Object.entries(value).map(([statistic, amount]) => [metric, statistic, amount]);
    }
    return [[metric, 'value', value]];
  });
  const csv = ['metric,statistic,value', ...rows.map((row) => row.map((value) => JSON.stringify(value ?? '')).join(','))].join('\n');
  const link = document.createElement('a');
  link.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  link.download = `ivs-test-${runId}-summary.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
}

function RunDetails({ run }: { run: TestRun }) {
  const queryClient = useQueryClient();
  const summary = useQuery({ queryKey: ['summary', run.id], queryFn: () => api.getSummary(run.id), refetchInterval: 10_000 });
  const telemetry = useQuery({ queryKey: ['telemetry', run.id], queryFn: () => api.getTelemetry(run.id), refetchInterval: 10_000 });
  const cloudWatch = useQuery({
    queryKey: ['cloudwatch', run.id],
    queryFn: () => api.getCloudWatchMetrics(run.id),
    refetchInterval: run.status === 'RUNNING' ? 60_000 : false,
  });
  const endRun = useMutation({
    mutationFn: () => api.endTestRun(run.id),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: ['test-runs'] }),
  });
  const chartData = useMemo(() => telemetry.data?.items.filter((item) => item.liveLatencyMs !== undefined || item.glassToGlassMs !== undefined) ?? [], [telemetry.data]);
  return (
    <section className="panel run-panel">
      <div className="section-heading"><div><span className="eyebrow">{run.status}</span><h2>{run.label}</h2></div><code>{run.id}</code></div>
      <div className="metric-cards">
        <div><span>Samples</span><strong>{summary.data?.sampleCount ?? 0}</strong></div>
        <div><span>Glass p95</span><strong>{summary.data?.glassToGlassMs.p95?.toFixed(0) ?? '—'} ms</strong></div>
        <div><span>Live p95</span><strong>{summary.data?.liveLatencyMs.p95?.toFixed(0) ?? '—'} ms</strong></div>
        <div><span>停止時間率</span><strong>{summary.data?.stoppageRatePercent.toFixed(2) ?? '—'} %</strong></div>
        <div><span>品質変更</span><strong>{summary.data?.qualityChangeCount ?? 0}</strong></div>
        <div><span>Errors</span><strong>{summary.data?.errorCount ?? 0}</strong></div>
      </div>
      <div className="chart">
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={chartData}><CartesianGrid strokeDasharray="3 3" stroke="#25354d" /><XAxis dataKey="timestamp" hide /><YAxis /><Tooltip /><Line type="monotone" dataKey="glassToGlassMs" stroke="#55e6a5" dot={false} /><Line type="monotone" dataKey="liveLatencyMs" stroke="#63a9ff" dot={false} /></LineChart>
        </ResponsiveContainer>
      </div>
      <h3>CloudWatch / IVS</h3>
      <div className="metric-cards">
        {cloudWatch.data?.items.map((series) => (
          <div key={series.metricName}>
            <span>{series.metricName} p95</span>
            <strong>{series.summary.p95?.toFixed(1) ?? '—'}</strong>
          </div>
        ))}
      </div>
      <div className="actions">
        <button onClick={() => downloadCsv(telemetry.data?.items ?? [], run.id)}>生データCSV</button>
        <button onClick={() => downloadSummaryCsv((summary.data ?? {}) as Record<string, unknown>, run.id)}>集計CSV</button>
        {run.status === 'RUNNING' && <button onClick={() => endRun.mutate()}>Runを終了</button>}
      </div>
    </section>
  );
}

function MeasurementsPage() {
  const queryClient = useQueryClient();
  const [broadcastId, setBroadcastId] = useState('');
  const [label, setLabel] = useState('');
  const [deviceTag, setDeviceTag] = useState('');
  const [browserTag, setBrowserTag] = useState('');
  const [networkTag, setNetworkTag] = useState('');
  const runs = useQuery({ queryKey: ['test-runs'], queryFn: api.listTestRuns });
  const broadcasts = useQuery({ queryKey: ['broadcasts'], queryFn: api.listBroadcasts });
  const createRun = useMutation({
    mutationFn: () =>
      api.createTestRun({
        broadcastId,
        label,
        tags: { device: deviceTag, browser: browserTag, network: networkTag },
      }),
    onSuccess: async () => { setLabel(''); await queryClient.invalidateQueries({ queryKey: ['test-runs'] }); },
  });
  return (
    <div>
      <header className="page-header"><div><span className="eyebrow">TELEMETRY</span><h1>計測</h1><p>任意の条件でRunを開始し、配信・視聴画面にRun IDを入力します。</p></div></header>
      <section className="panel">
        <div className="form-grid compact">
          <label>Broadcast<select value={broadcastId} onChange={(event) => setBroadcastId(event.target.value)}><option value="">選択</option>{broadcasts.data?.items.map((item) => <option key={item.id} value={item.id}>{item.name} / {modeLabel(item)}</option>)}</select></label>
          <label>ラベル<input value={label} onChange={(event) => setLabel(event.target.value)} placeholder="Wi-Fi / iPhone Safari など" /></label>
          <label>端末タグ<input value={deviceTag} onChange={(event) => setDeviceTag(event.target.value)} placeholder="iPhone 15" /></label>
          <label>ブラウザタグ<input value={browserTag} onChange={(event) => setBrowserTag(event.target.value)} placeholder="Safari 18" /></label>
          <label>回線タグ<input value={networkTag} onChange={(event) => setNetworkTag(event.target.value)} placeholder="Wi-Fi / 5G" /></label>
        </div>
        <button className="primary" disabled={!broadcastId || !label} onClick={() => createRun.mutate()}>計測Runを開始</button>
      </section>
      <div className="run-list">{runs.data?.items.map((run) => <RunDetails key={run.id} run={run} />)}</div>
    </div>
  );
}

export default Layout;
